/** UXP-00003 — Marketplace Connection Center framework (mirrors pkg/marketconnect) */

export const UXP_MC_ID = 'UXP-00003';

export interface MarketplaceSpec {
  id: string;
  name: string;
  country: string;
  region: string;
  health: number;
  enabled: boolean;
}

export interface SyncDomainSpec {
  domain: string;
  label: string;
  priority?: string;
  default: boolean;
}

export interface HealthMetric {
  label: string;
  status: 'healthy' | 'warning' | 'error';
  detail: string;
}

export const MARKETPLACE_CAPABILITIES = [
  'All Amazon Marketplaces', 'Marketplace Groups', 'Marketplace Health',
  'Marketplace Comparison', 'Marketplace Localization', 'Marketplace-specific Configuration',
];

export const AUTHORIZATION_STEPS = [
  'OAuth', 'Permission Validation', 'Connection Wizard', 'Marketplace Verification',
  'Token Refresh', 'Health Monitoring', 'Secure Credential Management',
];

export const SYNC_MODES = ['Real-time', 'Scheduled', 'Manual', 'Incremental', 'Full'];

export const SYNC_CONTROLS = ['Retry', 'Pause', 'Resume', 'Cancel', 'Priority Queues', 'Dependency Management'];

export const SYNC_DOMAINS: SyncDomainSpec[] = [
  { domain: 'catalog', label: 'Catalog', priority: 'critical', default: true },
  { domain: 'orders', label: 'Orders', priority: 'critical', default: true },
  { domain: 'inventory', label: 'Inventory', priority: 'critical', default: true },
  { domain: 'advertising', label: 'Advertising', priority: 'high', default: true },
  { domain: 'finance', label: 'Finance', priority: 'high', default: true },
  { domain: 'brand_analytics', label: 'Brand Analytics', priority: 'medium', default: false },
  { domain: 'business_reports', label: 'Business Reports', priority: 'medium', default: false },
  { domain: 'reviews', label: 'Reviews', priority: 'medium', default: false },
  { domain: 'questions', label: 'Questions', priority: 'low', default: false },
  { domain: 'promotions', label: 'Promotions', priority: 'medium', default: false },
  { domain: 'returns', label: 'Returns', priority: 'medium', default: false },
  { domain: 'fba', label: 'FBA', priority: 'high', default: true },
  { domain: 'brand_registry', label: 'Brand Registry', priority: 'medium', default: false },
  { domain: 'customer_growth', label: 'Customer Growth', priority: 'medium', default: false },
];

export const AI_INIT_ARTIFACTS = [
  'Brand DNA', 'Product DNA', 'Visual DNA', 'Content DNA', 'Prompt Rules', 'Brand Rules',
  'Image Rules', 'A+ Rules', 'Advertising Rules', 'Negative Rules', 'Style Guides',
  'Vocabulary', 'Tone', 'Terminology',
];

export const HEALTH_MONITORS = [
  'OAuth', 'Permissions', 'API Health', 'Rate Limits',
  'Background Workers', 'Synchronization', 'Marketplace Availability',
];

export const ERROR_FEATURES = [
  'Error Center', 'Retry Center', 'Recovery Guidance', 'AI Diagnostics', 'One-click Recovery',
];

export const WEBHOOK_CAPABILITIES = [
  'Subscriptions', 'Replay', 'Retries', 'Dead Events', 'Health', 'Audit',
];

export const ADVISOR_TOPICS = [
  'Synchronization Schedule', 'API Optimization', 'Marketplace Health',
  'Brand Initialization', 'Performance Improvements',
];

export const MARKETPLACES: MarketplaceSpec[] = [
  { id: 'ATVPDKIKX0DER', name: 'Amazon.com', country: 'US', region: 'north_america', health: 95, enabled: true },
  { id: 'A2EUQ1WTGCTBG2', name: 'Amazon.ca', country: 'CA', region: 'north_america', health: 92, enabled: false },
  { id: 'A1AM78C64UM0Y8', name: 'Amazon.com.mx', country: 'MX', region: 'north_america', health: 88, enabled: false },
  { id: 'A1F83G8C2ARO7P', name: 'Amazon.co.uk', country: 'UK', region: 'europe', health: 91, enabled: false },
  { id: 'A1PA6795UKMFR9', name: 'Amazon.de', country: 'DE', region: 'europe', health: 90, enabled: false },
  { id: 'A21TJRUUN4KGV', name: 'Amazon.in', country: 'IN', region: 'far_east', health: 94, enabled: true },
  { id: 'A1VC38T7YXB528', name: 'Amazon.co.jp', country: 'JP', region: 'far_east', health: 88, enabled: false },
];

export const DEMO_ADVISOR_RECS = [
  'Schedule catalog sync every 4 hours; inventory every 2 hours during peak season',
  'Reduce API calls by 23% — batch report requests during off-peak hours',
  'Amazon.in health score 94 — enable advertising sync for full coverage',
  'Brand DNA initialization ready — 847 products analyzed, 91% confidence',
  'Enable webhook subscriptions for ORDER_CHANGE to reduce polling latency',
];

export const DEMO_ERRORS = [
  { code: 'SPAPI_RATE_LIMIT', message: 'Rate limit exceeded on catalog items API — auto-retry in 60s', retryable: true },
  { code: 'TOKEN_EXPIRING', message: 'Refresh token expires in 7 days — re-authorization recommended', retryable: false },
  { code: 'SYNC_PARTIAL', message: 'Inventory sync completed with 3 SKU failures', retryable: true },
];

export const DEMO_SYNC_JOBS = [
  { id: 'j1', domain: 'catalog', mode: 'incremental', state: 'completed', progress: 100 },
  { id: 'j2', domain: 'inventory', mode: 'full', state: 'running', progress: 67 },
  { id: 'j3', domain: 'orders', mode: 'incremental', state: 'queued', progress: 0 },
  { id: 'j4', domain: 'advertising', mode: 'full', state: 'failed', progress: 42 },
];

export const ARCHITECTURE_STAGES = [
  'OAuth Wizard', 'Permission Validation', 'Marketplace Selection', 'Token Vault',
  'Sync Engine', 'AI Brand Initialization', 'Connection Health', 'Error Center',
  'Webhook Hub', 'AI Marketplace Advisor',
];

export const ANALYTICS_METRICS = [
  'Sync Success', 'API Performance', 'Marketplace Health', 'Connection Health',
  'AI Initialization', 'Queue Performance',
];

export interface MarketConnectDashboard {
  marketplace_capabilities: number;
  authorization_steps: number;
  sync_domains: number;
  sync_modes: number;
  ai_init_artifacts: number;
  health_monitors: number;
  webhook_capabilities: number;
  advisor_topics: number;
}

export function buildMarketConnectDashboard(): MarketConnectDashboard {
  return {
    marketplace_capabilities: MARKETPLACE_CAPABILITIES.length,
    authorization_steps: AUTHORIZATION_STEPS.length,
    sync_domains: SYNC_DOMAINS.length,
    sync_modes: SYNC_MODES.length,
    ai_init_artifacts: AI_INIT_ARTIFACTS.length,
    health_monitors: HEALTH_MONITORS.length,
    webhook_capabilities: WEBHOOK_CAPABILITIES.length,
    advisor_topics: ADVISOR_TOPICS.length,
  };
}

export const LIVE_ROUTES = {
  connect: '/amazon/connect',
} as const;

export const WIZARD_STEPS = ['Request', 'Authorize', 'Marketplaces', 'Approve', 'Done'];
